import React, { useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ServiceAreaMap.css';

const CARSON_CENTER = [33.8317, -118.2620];
const RADIUS_MILES = 12;
const RADIUS_METERS = RADIUS_MILES * 1609.34;
const CITY_RADIUS_METERS = 2 * 1609.34;

const markerIcon = L.divIcon({
  className: 'map-marker-icon',
  html: `<div class="map-marker-pulse"></div><div class="map-marker-dot"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const nearbyAreas = [
  { name: 'Long Beach', coords: [33.7701, -118.1937] },
  { name: 'Torrance', coords: [33.8358, -118.3406] },
  { name: 'Compton', coords: [33.8958, -118.2201] },
  { name: 'Lakewood', coords: [33.8536, -118.1340] },
  { name: 'Inglewood', coords: [33.9617, -118.3531] },
  { name: 'Downey', coords: [33.9401, -118.1332] },
  { name: 'Hawthorne', coords: [33.9164, -118.3526] },
  { name: 'Wilmington', coords: [33.7839, -118.2648] },
  { name: 'Gardena', coords: [33.8883, -118.3090] },
  { name: 'Paramount', coords: [33.8894, -118.1597] },
];

const areaIcon = L.divIcon({
  className: 'map-area-icon',
  html: '',
  iconSize: [8, 8],
  iconAnchor: [4, 4],
});

function ScrollZoomToggle() {
  const map = useMap();
  const mapContainer = map.getContainer();

  React.useEffect(() => {
    const enableScroll = () => map.scrollWheelZoom.enable();
    const disableScroll = () => map.scrollWheelZoom.disable();

    mapContainer.addEventListener('mouseenter', enableScroll);
    mapContainer.addEventListener('mouseleave', disableScroll);

    return () => {
      mapContainer.removeEventListener('mouseenter', enableScroll);
      mapContainer.removeEventListener('mouseleave', disableScroll);
    };
  }, [map, mapContainer]);

  return null;
}

function FitBounds() {
  const map = useMap();
  React.useEffect(() => {
    map.setView(CARSON_CENTER, 11);
  }, [map]);
  return null;
}

function FlyToCity({ target, onArrived }) {
  const map = useMap();
  React.useEffect(() => {
    if (target) {
      map.flyTo(target.coords, 14, { duration: 1.2 });
    }
  }, [target, map]);
  return null;
}

function FlyToOverview({ trigger }) {
  const map = useMap();
  React.useEffect(() => {
    if (trigger > 0) {
      map.flyTo(CARSON_CENTER, 12, { duration: 1.2 });
    }
  }, [trigger, map]);
  return null;
}

const ServiceAreaMap = () => {
  const [activeCity, setActiveCity] = useState(null);
  const [resetTrigger, setResetTrigger] = useState(0);

  const handleCityClick = useCallback((area) => {
    setActiveCity(area);
  }, []);

  const handleReset = useCallback(() => {
    setActiveCity(null);
    setResetTrigger(prev => prev + 1);
  }, []);

  return (
    <section className="service-area" id="service-area">
      <div className="service-area-content">
        <div className="service-area-text">
          <div className="section-label">Where we operate</div>
          <h2 className="service-area-heading">
            Serving Carson<br /><em>&amp; surrounding areas.</em>
          </h2>
          <p className="service-area-desc">
            We come to you. Our technicians cover a wide area around
            Carson, CA which includes most of the South Bay, Long Beach,
            and surrounding communities.
          </p>
          <div className="service-area-stats">
            <div className="service-area-stat">
              <span className="service-area-stat-value">South Bay</span>
              <span className="service-area-stat-label">& Beyond</span>
            </div>
            <div className="service-area-stat-divider" />
            <div className="service-area-stat">
              <span className="service-area-stat-value">Same-Day</span>
              <span className="service-area-stat-label">House Calls</span>
            </div>
            <div className="service-area-stat-divider" />
            <div className="service-area-stat">
              <span className="service-area-stat-value">10+</span>
              <span className="service-area-stat-label">Cities Covered</span>
            </div>
          </div>
          <div className="service-area-cities">
            <div className="service-area-cities-label">
              Cities we service include:
              {activeCity && (
                <button className="service-area-reset-btn" onClick={handleReset}>
                  ← Back to overview
                </button>
              )}
            </div>
            <div className="service-area-cities-grid">
              {nearbyAreas.map(area => (
                <span
                  key={area.name}
                  className={`service-area-city${activeCity?.name === area.name ? ' service-area-city--active' : ''}`}
                  onClick={() => handleCityClick(area)}
                >
                  {area.name}
                </span>
              ))}
              <span className="service-area-city service-area-city--more">+ more</span>
            </div>
          </div>
        </div>
        <div className="service-area-map-wrap">
          <MapContainer
            center={CARSON_CENTER}
            zoom={12}
            scrollWheelZoom={false}
            zoomControl={false}
            attributionControl={false}
            className="service-area-map"
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
            />
            <FitBounds />
            <ScrollZoomToggle />
            <FlyToCity target={activeCity} />
            <FlyToOverview trigger={resetTrigger} />

            <Circle
              center={CARSON_CENTER}
              radius={RADIUS_METERS}
              pathOptions={{
                color: '#DC2626',
                weight: 1.5,
                fillColor: '#DC2626',
                fillOpacity: 0.08,
                dashArray: '6 4',
              }}
            />

            <Circle
              center={CARSON_CENTER}
              radius={RADIUS_METERS * 0.5}
              pathOptions={{
                color: '#DC2626',
                weight: 1,
                fillColor: '#DC2626',
                fillOpacity: 0.05,
                dashArray: '3 3',
                opacity: 0.4,
              }}
            />

            {activeCity && (
              <Circle
                center={activeCity.coords}
                radius={CITY_RADIUS_METERS}
                pathOptions={{
                  color: '#EF4444',
                  weight: 2,
                  fillColor: '#EF4444',
                  fillOpacity: 0.12,
                }}
              />
            )}

            <Marker position={CARSON_CENTER} icon={markerIcon}>
              <Popup className="map-popup">
                <strong>MTS HQ</strong><br />Carson, CA
              </Popup>
            </Marker>

            {nearbyAreas.map(area => (
              <Marker
                key={area.name}
                position={area.coords}
                icon={areaIcon}
                eventHandlers={{
                  click: () => handleCityClick(area),
                }}
              >
                <Popup className="map-popup">
                  <strong>{area.name}</strong><br />We service this area
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          <div className="map-overlay-gradient" />
          {activeCity && (
            <button className="map-reset-btn" onClick={handleReset}>
              ← Full service area
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;
